import { combineEpics, ofType } from 'redux-observable'
import { forkJoin, of } from 'rxjs'
import { catchError, filter, map, mergeMap } from 'rxjs/operators'

import {
  fetchKillswitchStatusError,
  fetchKillswitchStatusSuccess,
  fetchOriginalSubjectError,
  fetchOriginalSubjectSuccess,
  fetchSettings,
  fetchSettingsError,
  fetchSettingsSuccess,
  saveSettingsError,
  saveSettingsSuccess,
  updateKillswitchError,
  updateKillswitchSuccess,
  updateOriginalSubjectError,
  updateOriginalSubjectSuccess,
  updateSilentModeError,
  updateSilentModeSuccess,
  updateStatusError,
  updateStatusSuccess,
} from './actions'
import {
  FETCH_KILLSWITCH_ASYNC,
  FETCH_ORIGINAL_SUBJECT_ASYNC,
  FETCH_SETTINGS_ASYNC,
  SAVE_SETTINGS_ASYNC,
  UPDATE_KILLSWITCH_ASYNC,
  UPDATE_ORIGINAL_SUBJECT_ASYNC,
  UPDATE_SILENT_MODE_ASYNC,
  UPDATE_STATUS_ASYNC,
} from './constants'

const getCompanyId = (state$) => state$.value.partnerOptions?.selectedCompany?.value || ''

const getUserEmail = (state$) => {
  const state = state$.value
  return state.app?.data?.user?.email
}

const unwrapResponse = (response) => response?.response ?? response?.data ?? response

const POWERSHELL_DISABLED = { status: 0 }
const POWERSHELL_STATUS_UNKNOWN = { status: null }

const isPowershellStatusUnknownError = (error) => Number(error?.status) === 500

const toPowershellEnabled = (psStatus) => {
  if (psStatus?.status === 1) return true
  if (psStatus?.status === 0) return false
  return null
}

const fetchSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_SETTINGS_ASYNC.INITIATE),
    mergeMap(() => {
      const companyId = getCompanyId(state$)

      return forkJoin(
        api.settings.getEmailEncryptionConfig(companyId),
        api.settings.getEmailEncryptionSettings(companyId),
        api.settings
          .getPowershellStatus(companyId)
          .pipe(
            catchError((error) =>
              of(
                isPowershellStatusUnknownError(error)
                  ? POWERSHELL_STATUS_UNKNOWN
                  : POWERSHELL_DISABLED
              )
            )
          )
      ).pipe(
        map(([configResponse, entitlementResponse, psStatusResponse]) => {
          const config = unwrapResponse(configResponse)
          const entitlement = unwrapResponse(entitlementResponse)
          const psStatus = unwrapResponse(psStatusResponse)

          return fetchSettingsSuccess({
            ...config,
            _entitlement: entitlement,
            _powershellEnabled: toPowershellEnabled(psStatus),
          })
        }),
        catchError((error) => of(epicsErrorHandler(fetchSettingsError, error, [404])))
      )
    })
  )

const fetchKillswitchEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_KILLSWITCH_ASYNC.INITIATE),
    mergeMap(() => {
      const companyId = getCompanyId(state$)

      return api.settings.getEmailEncryptionKillswitch(companyId).pipe(
        map((response) => fetchKillswitchStatusSuccess(unwrapResponse(response))),
        catchError((error) => of(epicsErrorHandler(fetchKillswitchStatusError, error)))
      )
    })
  )

const updateStatusEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_STATUS_ASYNC.INITIATE),
    mergeMap(({ payload: status }) => {
      const companyId = getCompanyId(state$)
      const isSuspended = status === 'SUSPENDED'

      return api.settings
        .updateEmailEncryptionConfig(companyId, {
          status,
          mtaConfig: { bypassEnabled: isSuspended },
        })
        .pipe(
          map((response) => updateStatusSuccess(response)),
          catchError((error) => of(epicsErrorHandler(updateStatusError, error)))
        )
    })
  )

const saveSettingsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_SETTINGS_ASYNC.INITIATE),
    mergeMap(({ payload }) => {
      const companyId = getCompanyId(state$)
      const userEmail = getUserEmail(state$)

      const encryptionModeArray = []
      if (payload.userControlled) encryptionModeArray.push('keyword')
      if (payload.ruleBased) encryptionModeArray.push('policy_based')

      const finalPayload = {
        policyEngineConfig: {
          encryptionMode: encryptionModeArray,
        },
        user: userEmail,
      }

      return api.settings.updateEmailEncryptionConfig(companyId, finalPayload).pipe(
        mergeMap((response) => [saveSettingsSuccess(response), fetchSettings()]),
        catchError((error) => of(epicsErrorHandler(saveSettingsError, error)))
      )
    })
  )

const updateSilentModeEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_SILENT_MODE_ASYNC.INITIATE),
    mergeMap(({ payload: silentMode }) => {
      const companyId = getCompanyId(state$)

      return api.settings
        .updateEmailEncryptionSettings({ dlpSilentMode: silentMode }, companyId)
        .pipe(
          map((response) => {
            const body = unwrapResponse(response)
            return updateSilentModeSuccess({
              ...(body && typeof body === 'object' ? body : {}),
              dlpSilentMode: silentMode === true,
            })
          }),
          catchError((error) => of(epicsErrorHandler(updateSilentModeError, error)))
        )
    })
  )

const updateKillswitchEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_KILLSWITCH_ASYNC.INITIATE),
    mergeMap(({ payload: killswitchAction }) => {
      const companyId = getCompanyId(state$)

      return api.settings.updateEmailEncryptionKillswitch(companyId, killswitchAction).pipe(
        map((response) =>
          updateKillswitchSuccess({
            ...unwrapResponse(response),
            requestedAction: killswitchAction,
          })
        ),
        catchError((error) => of(epicsErrorHandler(updateKillswitchError, error)))
      )
    })
  )

// The response is dropped when the selected company changed while the request was in flight,
// so one tenant's flag never lands in another tenant's switch. The PUT itself still completes.
const forCompanyStill = (state$, companyId) => filter(() => getCompanyId(state$) === companyId)

const fetchOriginalSubjectEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_ORIGINAL_SUBJECT_ASYNC.INITIATE),
    mergeMap(() => {
      const companyId = getCompanyId(state$)

      return api.settings.getEmailEncryptionBranding(companyId).pipe(
        map((response) =>
          fetchOriginalSubjectSuccess(unwrapResponse(response)?.useOriginalSubject === true)
        ),
        catchError((error) => of(epicsErrorHandler(fetchOriginalSubjectError, error))),
        forCompanyStill(state$, companyId)
      )
    })
  )

const updateOriginalSubjectEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_ORIGINAL_SUBJECT_ASYNC.INITIATE),
    mergeMap(({ payload: useOriginalSubject }) => {
      const companyId = getCompanyId(state$)

      return api.settings.updateEmailEncryptionBranding(companyId, { useOriginalSubject }).pipe(
        map((response) =>
          updateOriginalSubjectSuccess(unwrapResponse(response)?.useOriginalSubject === true)
        ),
        catchError((error) => of(epicsErrorHandler(updateOriginalSubjectError, error))),
        forCompanyStill(state$, companyId)
      )
    })
  )

export default combineEpics(
  fetchSettingsEpic,
  fetchKillswitchEpic,
  updateStatusEpic,
  updateSilentModeEpic,
  saveSettingsEpic,
  updateKillswitchEpic,
  fetchOriginalSubjectEpic,
  updateOriginalSubjectEpic
)
